import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;

    if (this.autoplayWhenVisible) {
      // Muted ambient video: it plays/pauses with its own visibility and must
      // not be paused when another media starts (nor pause the others).
      this.#observeVisibility(signal);
      return;
    }

    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  /**
   * `autoplay-when-visible`: load the (muted) video the first time it is mostly
   * on screen, play it while visible and pause it when it scrolls or slides out.
   * Works inside carousels too, since the observer accounts for the scroller clipping.
   * @type {boolean}
   */
  get autoplayWhenVisible() {
    return this.hasAttribute('autoplay-when-visible');
  }

  /**
   * Baymo: whether the autoplay video should be playing right now (>= 50% on screen).
   * Kept as state so every later signal (new threshold, data loaded, slide settled,
   * tab visible again) can retry play() instead of relying on a single attempt.
   * @type {boolean}
   */
  #wantsToPlay = false;

  /** Baymo: the shopper paused it with the play/pause button; respected until it leaves the screen */
  #userPaused = false;

  /** @param {AbortSignal} signal */
  #observeVisibility(signal) {
    // Several thresholds so the callback fires again when a carousel slide finishes
    // settling (ratio 1). With only [0, 0.5] it fired once mid-swipe: the video was
    // created and play() was attempted before the browser considered it visible,
    // the attempt was dropped and nothing retried until the slide left and came back.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > 0) {
            // Start fetching as soon as it peeks in, so it is ready when centred
            this.loadContent(false);
          }

          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            if (this.#userPaused) continue;
            this.#wantsToPlay = true;
            this.playMedia();
          } else {
            this.#wantsToPlay = false;
            this.#userPaused = false;
            this.#retryCount = 0;
            if (this.getAttribute('data-media-loaded')) this.pauseMedia();
          }
        }
      },
      { threshold: [0, 0.01, 0.25, 0.5, 0.75, 0.99, 1] }
    );

    // Baymo: free the decoded video once it is well off screen *vertically* (infinite
    // scroll on iOS otherwise piles up dozens of <video> elements until Safari kills the
    // tab). Slides that just left a horizontal carousel are kept, so swiping back does
    // not recreate the video. It is recreated from the <template> when it comes back.
    const unloadObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) continue;
          const rect = entry.boundingClientRect;
          const viewportHeight = window.innerHeight;
          const farAway = rect.bottom < -1.5 * viewportHeight || rect.top > 2.5 * viewportHeight;
          if (farAway) this.#unloadContent();
        }
      },
      { rootMargin: '150% 0px 150% 0px' }
    );

    observer.observe(this);
    unloadObserver.observe(this);

    // A carousel finishing its scroll, or the tab coming back, is another chance to play
    const retry = () => this.#retryPlay();
    const scroller = this.closest('slideshow-slides');
    scroller?.addEventListener('scrollend', retry, { signal, passive: true });
    document.addEventListener('visibilitychange', retry, { signal });

    signal.addEventListener('abort', () => {
      observer.disconnect();
      unloadObserver.disconnect();
    });
  }

  /** Plays the loaded autoplay video if it should be playing and is not */
  #retryPlay() {
    if (!this.#wantsToPlay || document.hidden) return;
    const video = this.querySelector(':scope > video');
    if (video instanceof HTMLVideoElement && video.paused) this.#safePlay(video);
  }

  /** Removes a loaded autoplay video (keeps the poster/template so it can be reloaded) */
  #unloadContent() {
    if (!this.autoplayWhenVisible || !this.getAttribute('data-media-loaded')) return;
    const video = this.querySelector(':scope > video');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      for (const source of video.querySelectorAll('source')) source.removeAttribute('src');
      video.load();
      video.remove();
    }
    this.removeAttribute('data-media-loaded');
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
    this.isPlaying = false;
    this.#wantsToPlay = false;
  }

  /**
   * iOS Safari only autoplays inline when the video is muted *as a property* and inline.
   * A node cloned from a <template> lives in the template's inert document, and WebKit
   * may not apply the muted/autoplay attributes after adopting it, so play() is rejected
   * silently and the poster stays. Force them and retry when the video can play.
   * @param {HTMLVideoElement} video
   */
  #prepareInlineVideo(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    // Baymo: play/pause is driven only by our observer. With the native `autoplay`
    // attribute the browser starts it on its own (e.g. while a slide is only peeking)
    // and applies its own off-screen pausing, which fought with ours.
    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.preload = 'auto';
  }

  /** Baymo: pending back-off retry, and how many were used in this visit (reset when it leaves the screen) */
  #retryTimer = 0;
  #retryCount = 0;

  /** @param {HTMLVideoElement} video */
  #safePlay(video) {
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        // Not ready yet, or refused because the browser did not consider the video
        // visible at that instant (iOS WebKit does this mid-swipe). Keep trying with a
        // short back-off while it should be playing. Low Power Mode on iOS blocks
        // autoplay entirely; the poster stays in that case.
        this.#scheduleRetry();
      });
    }
  }

  #scheduleRetry() {
    if (!this.autoplayWhenVisible || !this.#wantsToPlay || this.#retryTimer) return;
    const delays = [150, 400, 800, 1500, 3000];
    if (this.#retryCount >= delays.length) return;
    this.#retryTimer = window.setTimeout(() => {
      this.#retryTimer = 0;
      this.#retryCount += 1;
      this.#retryPlay();
    }, delays[this.#retryCount]);
  }

  /**
   * Retry play() whenever the video gets data or the browser pauses it on its own while
   * it should still be playing (WebKit/Chrome pause muted autoplay video they think is
   * off screen, and do not always resume it).
   * @param {HTMLVideoElement} video
   */
  #bindRetries(video) {
    const retry = () => this.#retryPlay();
    for (const type of ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough']) {
      video.addEventListener(type, retry);
    }
    video.addEventListener('pause', () => {
      if (this.#wantsToPlay && !video.ended) this.#scheduleRetry();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    if (!this.autoplayWhenVisible) this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const templateNode = this.querySelector('template')?.content.firstElementChild;
    // importNode creates it in the page document (see #prepareInlineVideo)
    const content = templateNode ? document.importNode(templateNode, true) : null;

    if (!content) return;

    const nativeAutoplay = content instanceof HTMLVideoElement && content.hasAttribute('autoplay');

    if (content instanceof HTMLVideoElement && this.autoplayWhenVisible) {
      this.#prepareInlineVideo(content);
      this.#bindRetries(content);
    }

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && nativeAutoplay) {
      // force autoplay for safari (autoplay-when-visible videos only play once visible)
      if (!this.autoplayWhenVisible || this.#wantsToPlay) this.#safePlay(content);
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      if (this.autoplayWhenVisible) {
        this.#userPaused = true;
        this.#wantsToPlay = false;
      }
      this.pauseMedia();
    } else {
      if (this.autoplayWhenVisible) {
        this.#userPaused = false;
        this.#wantsToPlay = true;
      }
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        if (this.autoplayWhenVisible) this.#prepareInlineVideo(video);
        this.#safePlay(video);
      }
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
